import type { All8PageContent, IconCardGridComponent, TextCardGridComponent } from "@apa/core-renderers";
import { DEFAULT_PRERENDER_IMAGE } from "./pageComponentLibrary";

export type ComponentPreview = {
	id: string;
	title: string;
	description: string;
	components: All8PageContent;
	span?: "full";
};

export const COMPONENT_PREVIEWS: ComponentPreview[] = [
	{
		id: "hero",
		title: "Hero",
		description: "Landing hero with background image and lead copy.",
		span: "full",
		components: [
			{
				componentType: "Hero",
				key: "hero",
				content: {
					eyebrow: "Pièces diesel · Échange standard",
					title: "La performance d’origine. Le prix du reconditionné.",
					subtitle: "Turbos, injecteurs et FAP/DPF contrôlés dans nos ateliers, garantis jusqu’à 12 mois et expédiés sous 24/48h.",
					body: "Hero body text",
					primaryLabel: "Trouver ma pièce",
					primaryHref: { slug: "pieces-detachees" },
					secondaryLabel: "Parler à un spécialiste",
					secondaryHref: { slug: "contact" },
					assurances: ["Neuf ou reconditionné", "Garantie jusqu’à 12 mois", "Expédition 24/48h"],
					backgroundImageSrc: "images/photos/landing-apa-team.jpg",
					mobileBackgroundImageSrc: "images/photos/landing-apa-team-mobile.jpg"
				}
			}
		]
	},
	{
		id: "news",
		title: "News Section",
		description: "Featured story with supporting headlines.",
		components: [
			{
				componentType: "NewsSection",
				key: "news",
				content: {
					featured: {
						id: "feature-1",
						title: "Featured title",
						excerpt: "Featured excerpt",
						image: DEFAULT_PRERENDER_IMAGE,
						href: { slug: "/news/featured" }
					},
					items: [
						{
							id: "news-1",
							title: "News title 1",
							excerpt: "News excerpt 1",
							image: DEFAULT_PRERENDER_IMAGE,
							href: { slug: "/news/item-1" }
						},
						{
							id: "news-2",
							title: "News title 2",
							excerpt: "News excerpt 2",
							image: DEFAULT_PRERENDER_IMAGE,
							href: { slug: "/news/item-2" }
						},
						{
							id: "news-3",
							title: "News title 3",
							excerpt: "News excerpt 3",
							image: DEFAULT_PRERENDER_IMAGE,
							href: { slug: "/news/item-3" }
						}
					]
				}
			}
		]
	},
	{
		id: "news-list",
		title: "News List",
		description: "Vertical list of news items with optional gray panels.",
		components: [
			{
				componentType: "NewsList",
				key: "news-list",
				content: {
					useGrayPanel: true,
					items: [
						{
							tag: "TAG 1",
							title: "News title 1",
							excerpt: "News excerpt 1",
							date: "01/2025",
							href: { slug: "/news/item-1" }
						},
						{
							tag: "TAG 2",
							title: "News title 2",
							meta: "Meta line",
							date: "02/2025",
							href: { slug: "/news/item-2" }
						}
					],
					ctaLabel: "All news",
					ctaHref: { slug: "/news" }
				}
			}
		]
	},
	{
		id: "stats-summary",
		title: "Stats Summary",
		description: "Key rates and surveys at a glance.",
		components: [
			{
				componentType: "StatsSummary",
				key: "stats",
				content: {
					interestRates: [
						{ label: "Rate 1", value: "0.00%" },
						{ label: "Rate 2", value: "0.00%" },
						{ label: "Rate 3", value: "0.00%" }
					],
					exchangeRates: [
						{ code: "CUR1", value: "0.00" },
						{ code: "CUR2", value: "0.00" },
						{ code: "CUR3", value: "0.00" }
					],
					surveys: [
						{ label: "Survey 1", value: "0" },
						{ label: "Survey 2", value: "0" }
					]
				}
			}
		]
	},
	{
		id: "audience-tools",
		title: "Audience Tools",
		description: "Quick entry points by audience segment.",
		components: [
			{
				componentType: "AudienceTools",
				key: "audiences",
				content: {
					searchPlaceholder: "Search",
					audiences: [
						{ icon: "User", label: "Audience 1", href: { slug: "/audience-1" } },
						{ icon: "Building2", label: "Audience 2", href: { slug: "/audience-2" } },
						{ icon: "Megaphone", label: "Audience 3", href: { slug: "/audience-3" } },
						{ icon: "Landmark", label: "Audience 4", href: { slug: "/audience-4" } },
						{ icon: "FlaskConical", label: "Audience 5", href: { slug: "/audience-5" } },
						{ icon: "GraduationCap", label: "Audience 6", href: { slug: "/audience-6" } }
					]
				}
			}
		]
	},
	{
		id: "search-bar",
		title: "Search Bar",
		description: "Light blue bar with a centered, full-width search field.",
		span: "full",
		components: [
			{
				componentType: "SearchBar",
				key: "search-bar",
				content: {
					title: "Search",
					searchPlaceholder: "Search"
				}
			}
		]
	},
	{
		id: "search-bar-bold",
		title: "Search Bar (bold)",
		description: "Secondary blue background (#005CA9) with white title and icon.",
		span: "full",
		components: [
			{
				componentType: "SearchBar",
				key: "search-bar-bold",
				content: {
					title: "Search",
					searchPlaceholder: "Search",
					bold: true
				}
			}
		]
	},
	{
		id: "publications",
		title: "Publications + Stat Updates",
		description: "Dual column feed of publications and statistics.",
		span: "full",
		components: [
			{
				componentType: "PublicationsUpdates",
				key: "pub-updates",
				content: {
					backgroundImageSrc: DEFAULT_PRERENDER_IMAGE,
					publications: [
						{
							id: "pub-1",
							tag: "Tag 1",
							date: "01/2025",
							title: "Publication title 1",
							href: { slug: "/publications/item-1" }
						},
						{
							id: "pub-2",
							tag: "Tag 2",
							date: "02/2025",
							title: "Publication title 2",
							href: { slug: "/publications/item-2" }
						},
						{
							id: "pub-3",
							tag: "Tag 3",
							date: "03/2025",
							title: "Publication title 3",
							href: { slug: "/publications/item-3" }
						}
					],
					statUpdates: [
						{
							category: "Category",
							period: "2025-01",
							items: [
								{ title: "Update 1" },
								{ title: "Update 2" }
							]
						},
						{
							category: "Category",
							period: "2025-01",
							items: [
								{ title: "Update 3", subtitle: "Subtitle" },
								{ title: "Update 4" }
							]
						},
						{
							category: "Category",
							period: "2025-01",
							items: [
								{ title: "Update 5" },
								{ title: "Update 6" }
							]
						}
					]
				}
			}
		]
	},
	{
		id: "activity-grid",
		title: "Activity Grid",
		description: "Six-card grid from the Activités section.",
		components: [
			{
				componentType: "ActivityGrid",
				key: "activities-grid",
				content: {
					items: [
						{
							title: "Activity 1",
							description: "Activity description 1",
							href: { slug: "/activity-1" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						},
						{
							title: "Activity 2",
							description: "Activity description 2",
							href: { slug: "/activity-2" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						},
						{
							title: "Activity 3",
							description: "Activity description 3",
							href: { slug: "/activity-3" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						},
						{
							title: "Activity 4",
							description: "Activity description 4",
							href: { slug: "/activity-4" }
						},
						{
							title: "Activity 5",
							description: "Activity description 5",
							href: { slug: "/activity-5" }
						},
						{
							title: "Activity 6",
							description: "Activity description 6",
							href: { slug: "/activity-6" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						}
					]
				}
			}
		]
	},
	{
		id: "person-card-grid",
		title: "Person Cards Grid",
		description: "Grid of person/role cards with subtitle in #0095DB and no arrow.",
		components: [
			{
				componentType: "PersonCardGrid",
				key: "person-cards-grid",
				content: {
					items: [
						{
							title: "Mr. BATETELE BOLEKI LINKA Thomas",
							description: "Président du Collège des Commissaires aux Comptes",
							href: { slug: "/person-1" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						},
						{
							title: "Mr. ELONGO ONGONA Jean",
							description: "Membre du Collège des Commissaires aux Comptes",
							href: { slug: "/person-2" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						},
						{
							title: "Mme POLIPOLI LUNDA Chimène",
							description: "Membre du Collège des Commissaires aux Comptes",
							href: { slug: "/person-3" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						}
					]
				}
			}
		]
	},
	{
		id: "legislation-list",
		title: "Legislation / Document List",
		description: "List of underlined links with download icon (#FFA401). Icon sits next to the text on desktop.",
		components: [
			{
				componentType: "SectionHeading",
				key: "cadre-heading",
				content: { text: "Le cadre juridique", size: "medium" }
			},
			{
				componentType: "LegislationList",
				key: "legislation-list",
				content: {
					items: [
						{ label: "Loi organique n° 18-027 du 13 décembre 2018", href: { slug: "/docs/loi-18-027" } },
						{ label: "Statuts de la Banque Centrale du Congo", href: { slug: "/docs/statuts" } },
						{ label: "Convention entre l'État congolais et la Banque centrale", href: { slug: "/docs/convention" } },
						{ label: "Code de déontologie", href: { slug: "/docs/code-deontologie" } },
						{ label: "Règlement d'ordre intérieur", href: { slug: "/docs/reglement" } }
					]
				}
			}
		]
	},
	{
		id: "numbered-list",
		title: "Numbered List",
		description: "Ordered list (1. 2. 3. …). Each item is a string; long items wrap to multiple lines.",
		components: [
			{
				componentType: "NumberedList",
				key: "numbered-list",
				content: {
					items: [
						"la mise en oeuvre de la politique monétaire de la Banque;",
						"l'octroi des crédits spéciaux visés à l'article 20 de loi organique n°18-027;",
						"les décisions en matière d'agrément des établissements de crédit et autres établissements financiers assujettis au contrôle de la Banque et la prise de toute mesure, sanction, ordre ou décision en vertu des législations de contrôle concernées;",
						"l'édiction des instructions;",
						"l'adoption du budget et les comptes annuels;",
						"les nominations et les promotions du personnel;",
						"l'organisation du régime complémentaire de retraite du personnel."
					]
				}
			}
		]
	},
	{
		id: "two-column-list",
		title: "Two-Column Text List",
		description: "Left text (bg #0095DB, white) and right text (bg #EDF7FC, #005CA9).",
		components: [
			{
				componentType: "TwoColumnList",
				key: "two-column-list",
				content: {
					items: [
						{ left: "Jeudi 1er janvier", right: "Nouvel an" },
						{ left: "Dimanche 4 janvier", right: "Journée des Martyrs" },
						{ left: "Vendredi 16 janvier", right: "Assassinat de Laurent-Désiré Kabila" },
						{ left: "Samedi 17 janvier", right: "Assassinat de Patrice Lumumba" },
						{ left: "Lundi 6 avril", right: "Kimbangu Day" },
						{ left: "Vendredi 1er mai", right: "Fête du Travail" },
						{ left: "Dimanche 17 mai", right: "Journée de la Libération / Forces armées" },
						{ left: "Mardi 30 juin", right: "Jour de l'Indépendance" },
						{ left: "Samedi 1er août", right: "Journée des Parents" },
						{ left: "Vendredi 25 décembre", right: "Noël" }
					]
				}
			}
		]
	},
	{
		id: "labeled-text-grid",
		title: "Labeled Text Grid",
		description: "Grid of text blocks: title #0095DB, lines black. 2/3/4 cols for tablet/desktop.",
		components: [
			{
				componentType: "LabeledTextGrid",
				key: "labeled-text-grid",
				content: {
					items: [
						{ title: "Adresse postale", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] },
						{ title: "Livraisons", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] }
					],
					cardsPerRowTablet: 2,
					cardsPerRowDesktop: 2
				}
			}
		]
	},
	{
		id: "company-report-grid",
		title: "Company Report Grid",
		description: "Grid of report cards (#0095DB): title, year, published date, download button. Cards per row configurable for tablet/desktop.",
		components: [
			{
				componentType: "CompanyReportGrid",
				key: "company-report-grid",
				content: {
					items: [
						{ title: "Rapport d'entreprise", year: "2025", publishedDate: "21-11-2025", href: { slug: "/docs/rapport-2025" } },
						{ title: "Rapport d'entreprise", year: "2024", publishedDate: "21-11-2024", href: { slug: "/docs/rapport-2024" } },
						{ title: "Rapport d'entreprise", year: "2023", publishedDate: "21-11-2023", href: { slug: "/docs/rapport-2023" } },
						{ title: "Rapport d'entreprise", year: "2022", publishedDate: "21-11-2022", href: { slug: "/docs/rapport-2022" } },
						{ title: "Rapport d'entreprise", year: "2021", publishedDate: "21-11-2021", href: { slug: "/docs/rapport-2021" } },
						{ title: "Rapport d'entreprise", year: "2020", publishedDate: "21-11-2020", href: { slug: "/docs/rapport-2020" } }
					],
					cardsPerRowTablet: 3,
					cardsPerRowDesktop: 3,
					downloadLabel: "Télécharger"
				}
			}
		]
	},
	{
		id: "events",
		title: "Event Grid",
		description: "Compact list of upcoming events with dates.",
		components: [
			{
				componentType: "EventGrid",
				key: "events-grid",
				content: {
					items: [
						{
							tag: "EVENT",
							title: "Event title 1",
							location: "Location",
							href: { slug: "/events/event-1" },
							startDay: 1,
							endDay: 1,
							monthLabel: "JAN"
						},
						{
							tag: "EVENT",
							title: "Event title 2",
							location: "Location",
							href: { slug: "/events/event-2" },
							startDay: 2,
							endDay: 2,
							monthLabel: "FEB"
						}
					],
					ctaLabel: "All events",
					ctaHref: { slug: "/events" }
				}
			}
		]
	},
	{
		id: "articles",
		title: "Article Grid",
		description: "Four article cards with author metadata.",
		components: [
			{
				componentType: "ArticleGrid",
				key: "articles-grid",
				content: {
					items: [
						{
							category: "Category",
							title: "Article title 1",
							excerpt: "Article excerpt 1",
							author: "Author",
							authorRole: "Role",
							date: "01 Jan 2025",
							href: { slug: "/articles/article-1" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						},
						{
							category: "Category",
							title: "Article title 2",
							excerpt: "Article excerpt 2",
							author: "Author",
							authorRole: "Role",
							date: "01 Jan 2025",
							href: { slug: "/articles/article-2" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						},
						{
							category: "Category",
							title: "Article title 3",
							excerpt: "Article excerpt 3",
							author: "Author",
							authorRole: "Role",
							date: "01 Jan 2025",
							href: { slug: "/articles/article-3" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						},
						{
							category: "Category",
							title: "Article title 4",
							excerpt: "Article excerpt 4",
							author: "Author",
							authorRole: "Role",
							date: "01 Jan 2025",
							href: { slug: "/articles/article-4" },
							imageSrc: DEFAULT_PRERENDER_IMAGE
						}
					],
					ctaLabel: "View all",
					ctaHref: { slug: "/articles" }
				}
			}
		]
	},
	{
		id: "cta-banner",
		title: "CTA Banner",
		description: "Banner with title and CTA link.",
		components: [
			{
				componentType: "CtaBanner",
				key: "cta-banner",
				content: {
					title: "Banner title",
					linkLabel: "Learn more",
					linkHref: { slug: "/link" }
				}
			}
		]
	},
	{
		id: "article-hero",
		title: "Article Hero",
		description: "Article header with image, metadata, and sharing.",
		components: [
			{
				componentType: "ArticleHero",
				key: "article-hero",
				content: {
					eyebrow: "Category",
					title: "Article title",
					summary: "Article summary",
					author: "Author",
					authorRole: "Role",
					publishDate: "01/2025",
					featuredImage: {
						src: DEFAULT_PRERENDER_IMAGE,
						alt: "Image"
					}
				}
			}
		]
	},
	{
		id: "icon-cards",
		title: "Icon Card Grid",
		description: "Policy pillars represented as icon tiles.",
		components: [
			{
				componentType: "IconCardGrid",
				key: "icon-cards",
				content: {
					items: [
						{ icon: "ShieldCheck", title: "Card title 1", text: "Card text 1" },
						{ icon: "Gauge", title: "Card title 2", text: "Card text 2" },
						{ icon: "Banknote", title: "Card title 3", text: "Card text 3" },
						{ icon: "Building2", title: "Card title 4", text: "Card text 4" },
						{ icon: "Database", title: "Card title 5", text: "Card text 5" },
						{ icon: "Target", title: "Card title 6", text: "Card text 6" }
					],
					cardsPerRowTablet: 2,
					cardsPerRowDesktop: 3
				}
			} as IconCardGridComponent
		]
	},
	{
		id: "full-width-image",
		title: "Full Width Image",
		description: "Single image spanning the full width of the content area.",
		components: [
			{
				componentType: "FullWidthImage",
				key: "full-width-image",
				content: {
					src: DEFAULT_PRERENDER_IMAGE,
					alt: "Full width image"
				}
			} as unknown as All8PageContent[number]
		]
	},
	{
		id: "split-callout",
		title: "Split Image Callout",
		description: "Two-column layout with CTA and rich image.",
		components: [
			{
				componentType: "SplitImageCallout",
				key: "split-callout",
				content: {
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "Image",
					title: "Callout title",
					text: "Callout text",
					ctaLabel: "Learn more",
					ctaHref: { slug: "/callout" }
				}
			}
		]
	},
	{
		id: "split-callout-transparent",
		title: "Split Image Callout (transparent)",
		description: "Transparent text panel (no background).",
		span: "full",
		components: [
			{
				componentType: "SplitImageCallout",
				key: "split-callout-transparent",
				content: {
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "Image",
					title: "Callout title",
					text: "Callout text",
					ctaLabel: "Learn more",
					ctaHref: { slug: "/callout" },
					calloutVariant: "transparent"
				}
			}
		]
	},
	{
		id: "split-callout-fullwidth",
		title: "Split Image Callout (full width bg)",
		description: "Full-width section; default bg #F3F7FC or light theme bg #EDF7FC, button #005CA9.",
		span: "full",
		components: [
			{
				componentType: "SplitImageCallout",
				key: "split-callout-fullwidth",
				content: {
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "Image",
					title: "Callout title",
					text: "Callout text",
					ctaLabel: "Learn more",
					ctaHref: { slug: "/callout" },
					calloutVariant: "fullWidth",
					panelBackground: "light",
					buttonColor: "blue"
				}
			}
		]
	},
	{
		id: "split-callout-fit-height",
		title: "Split Image Callout (image fit text height)",
		description: "On desktop/tablet the image height matches the text panel; both columns share the same height.",
		span: "full",
		components: [
			{
				componentType: "SplitImageCallout",
				key: "split-callout-fit-height",
				content: {
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "Image",
					title: "Callout title",
					text: "Callout text",
					ctaLabel: "Learn more",
					ctaHref: { slug: "/callout" },
					imageFitTextHeight: true
				}
			}
		]
	},
	{
		id: "text-card-grid",
		title: "Text Card Grid (3 per row)",
		description: "Cards centered; 3 per row on tablet/desktop.",
		components: [
			{
				componentType: "TextCardGrid",
				key: "text-card-grid",
				content: {
					items: [
						{ title: "Card title 1", text: "Card text 1" },
						{ title: "Card title 2", text: "Card text 2" },
						{ title: "Card title 3", text: "Card text 3" },
						{ title: "Card title 4", text: "Card text 4" }
					],
					cardsPerRowTablet: 3,
					cardsPerRowDesktop: 3
				}
			} as TextCardGridComponent
		]
	},
	{
		id: "text-card-grid-2-per-row",
		title: "Text Card Grid (2 per row)",
		description: "Cards centered; 2 per row on tablet/desktop.",
		components: [
			{
				componentType: "TextCardGrid",
				key: "text-card-grid-2",
				content: {
					items: [
						{ title: "Card title 1", text: "Card text 1" },
						{ title: "Card title 2", text: "Card text 2" }
					],
					cardsPerRowTablet: 2,
					cardsPerRowDesktop: 2
				}
			} as TextCardGridComponent
		]
	},
	{
		id: "text-card-grid-4-per-row",
		title: "Text Card Grid (4 per row)",
		description: "Cards centered; 4 per row on tablet/desktop.",
		components: [
			{
				componentType: "TextCardGrid",
				key: "text-card-grid-4",
				content: {
					items: [
						{ title: "Card title 1", text: "Card text 1" },
						{ title: "Card title 2", text: "Card text 2" },
						{ title: "Card title 3", text: "Card text 3" },
						{ title: "Card title 4", text: "Card text 4" }
					],
					cardsPerRowTablet: 4,
					cardsPerRowDesktop: 4
				}
			} as TextCardGridComponent
		]
	},
	{
		id: "text-with-side-panel",
		title: "Text With Side Panel",
		description: "Main text column with a side panel (title, image, CTA).",
		span: "full",
		components: [
			{
				componentType: "TextWithSidePanel",
				key: "text-side-panel",
				content: {
					title: "Section title",
					paragraphs: [
						"Paragraph 1",
						"Paragraph 2",
						"Paragraph 3"
					],
					panelTitle: "Panel title",
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "Image",
					ctaLabel: "Learn more",
					ctaHref: { slug: "/link" }
				}
			}
		]
	},
	{
		id: "accordion-section",
		title: "Accordion Section",
		description: "Expandable items (bg #EDF7FC, title #005CA9, plus #FFA401). Each item has title and rich paragraphs.",
		span: "full",
		components: [
			{
				componentType: "AccordionSection",
				key: "accordion",
				content: {
					items: [
						{
							title: "Quel est le meilleur moment pour nous rendre visite",
							paragraphs: ["Vous pouvez nous rendre visite du lundi au vendredi, de 8h à 17h. Il est préférable de prendre rendez-vous."]
						},
						{
							title: "Préparez votre visite",
							paragraphs: ["Prévoyez une pièce d'identité et, le cas échéant, les documents listés dans votre convocation."]
						},
						{
							title: "Itinéraire pour nous rejoindre",
							paragraphs: ["Notre siège est situé au 2697 Kinshasa 1. Transports en commun : lignes 12 et 45, arrêt Place de la Banque."]
						}
					]
				}
			}
		]
	},
	{
		id: "coordonnees",
		title: "Coordonnées",
		description: "Split: image left (fit text height), content right. Title #005CA9, phone #FFA401, email #005CA9. No rounded corners.",
		span: "full",
		components: [
			{
				componentType: "Coordonnees",
				key: "coordonnees",
				content: {
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "Siège APA",
					topTitle: "Les coordonnées générales",
					introParagraph: "Pour toute demande générale, vous pouvez contacter la Banque Centrale du Congo via les coordonnées ci-dessous :",
					phoneHref: { external: "tel:+243123456789" },
					emailHref: { external: "mailto:contact@apa.cd" },
					secondTitle: "Adresse et horaires",
					addressLinkText: "563 Boulevard Colonel Tshatshi - Gombé - Kinshasa",
					addressLinkHref: { external: "https://maps.google.com" },
					addressLines: ["Boîte Postale : 2697 Kinshasa 1 République Démocratique du Congo"]
				}
			}
		]
	},
	{
		id: "blue-callout",
		title: "Blue Callout",
		description: "Left panel #0095DB with white title, 16px paragraph, white chevron; right = image. Optional link.",
		span: "full",
		components: [
			{
				componentType: "BlueCallout",
				key: "blue-callout",
				content: {
					title: "Stabilité et dynamisme : la stratégie monétaire de la APA",
					text: "Pendant longtemps, le taux directeur à 25% a freiné l'inflation et stabilisé la monnaie. Les réserves obligatoires à 12-13% régulent la liquidité bancaire, tandis que les réserves de change couvrent 3 mois d'importations. En 2025, la APA a baissé son taux à 17,5%, encourageant le crédit et la reprise économique.",
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "Image"
				}
			}
		]
	}
];
